import cx from "classnames";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import { Dropdown } from "@gaming/xds-react-js";

import * as styles from "./SelectDropdown.module.css";

type SelectOption = Readonly<{
  id: string;
  label: string;
}>;

type SelectDropdownProps = Readonly<{
  options: SelectOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
}>;

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  className,
  disabled,
  "aria-label": ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  const selectedOption = options.find(o => o.id === value);
  const displayText = selectedOption?.label ?? placeholder ?? "";

  const toggle = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      onChange(id);
      close();
      triggerRef.current?.focus();
    },
    [onChange, close]
  );

  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
      } else if (e.key === "ArrowDown" && !isOpen) {
        e.preventDefault();
        setIsOpen(true);
      }
    },
    [close, isOpen]
  );

  const handleOptionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, id: string) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleSelect(id);
      } else if (e.key === "Escape") {
        close();
        triggerRef.current?.focus();
      } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const optionButtons = Array.from(
          listRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? []
        );
        const currentIndex = optionButtons.indexOf(e.currentTarget);
        const offset = e.key === "ArrowDown" ? 1 : -1;
        optionButtons[
          (currentIndex + offset + optionButtons.length) % optionButtons.length
        ]?.focus();
      }
    },
    [handleSelect, close]
  );

  // Focus first option when opened
  useEffect(() => {
    if (isOpen && listRef.current) {
      const firstOption = listRef.current.querySelector("button") as HTMLElement | null;
      firstOption?.focus();
    }
  }, [isOpen]);

  const menuMinWidth = triggerRef.current?.offsetWidth;

  return (
    <div
      className={cx(styles.wrapper, className)}
      style={
        {
          "--dropdown-min-width": menuMinWidth ? `${menuMinWidth}px` : "150px",
        } as React.CSSProperties
      }
    >
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        aria-label={ariaLabel ?? displayText}
        className={styles.trigger}
        onClick={toggle}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
      >
        <span>{displayText}</span>
        <svg
          className={cx(styles.chevron, isOpen && styles.chevronOpen)}
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {isOpen && (
        <Dropdown
          isOpen={isOpen}
          onClose={close}
          relativeRef={triggerRef as React.RefObject<HTMLElement>}
          backgroundDismissable
          dimBackground="none"
          className={styles.menu}
        >
          <ul ref={listRef} role="listbox" id={listboxId} className={styles.menuList}>
            {options.map(opt => (
              <li key={opt.id} role="option" aria-selected={opt.id === value}>
                <button
                  type="button"
                  className={cx(styles.option, opt.id === value && styles.optionSelected)}
                  onClick={() => handleSelect(opt.id)}
                  onKeyDown={e => handleOptionKeyDown(e, opt.id)}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </Dropdown>
      )}
    </div>
  );
};
